import currency from '|/helpers/currency';

export interface PayoffRow {
  id: 'offer' | 'balance' | 'result';
  label: string;
  /** Already signed for display — the panel flips signs by equity direction. */
  amount: number;
  note: string | null;
}

export interface TradeInView {
  isDeclined: boolean;
  /** Replaces the whole breakdown when the trade was declined. */
  declinedCopy: string;
  label: string;
  amount: number;
  isNegativeEquity: boolean;
  showPayoff: boolean;
  payoffRows: PayoffRow[];
  payoffBody: string;
}

export interface TradeInInput {
  isDeclined: boolean;
  offer: number;
  lienAmount: number;
  /** Portion of negative equity pushed into the down payment (`loan.details.tradeDown`). */
  tradeDown: number;
  /** Explicit modal state for whether part of the negative equity is financed. */
  isNegativeEquityFinanced?: boolean;
  isTaxSavingsEligible: boolean;
  stateName: string;
  lienHolder: string | null;
  /**
   * Positive equity reads differently without a Carvana loan: there is no down payment or
   * financed amount for the credit to reduce, so cash and SAF get the total-purchase-price
   * wording instead.
   */
  isCarvanaFinanced: boolean;
}

const money = (value: number) => currency(Math.abs(value));

/**
 * Positive equity leads with the Carvana offer and nets to "Your trade-in value"; negative
 * equity leads with the loan balance and nets to "Balance you still owe", flipping which
 * amount is shown negative. The declined frames drop the breakdown and the total row
 * entirely in favour of a single explanatory line.
 */
export const deriveTradeIn = ({
  isDeclined,
  offer,
  lienAmount,
  tradeDown,
  isNegativeEquityFinanced,
  isTaxSavingsEligible,
  stateName,
  lienHolder,
  isCarvanaFinanced
}: TradeInInput): TradeInView => {
  const net = offer - lienAmount;
  const isNegativeEquity = net < 0;
  const financesNegativeEquity = isNegativeEquityFinanced ?? tradeDown > 0;

  const declinedCopy = isTaxSavingsEligible
    ? `You declined adding a trade-in. You could save ${stateName} sales tax by including a trade-in.`
    : 'You declined adding a trade-in. Adding a trade-in can reduce or eliminate down and monthly payments.';

  const lenderCopy = lienHolder
    ? `Based on the estimated ${lienHolder} balance you provided.`
    : 'Based on the estimated loan balance you provided.';

  const offerRow: PayoffRow = {
    id: 'offer',
    label: 'Your Carvana offer',
    amount: isNegativeEquity ? -offer : offer,
    note: null
  };

  const balanceRow: PayoffRow = {
    id: 'balance',
    label: 'Trade-in loan balance',
    amount: isNegativeEquity ? lienAmount : -lienAmount,
    note: lenderCopy
  };

  const resultRow: PayoffRow = {
    id: 'result',
    label: isNegativeEquity ? 'Balance you still owe' : 'Your trade-in value',
    amount: Math.abs(net),
    note: null
  };

  const payoffBody = !isNegativeEquity
    ? isCarvanaFinanced
      ? `Your trade-in value of ${money(
          net
        )} will be applied to your purchase, reducing your down payment and the amount you need to finance.`
      : `Your trade-in value of ${money(
          net
        )} will be applied to your purchase, reducing your total purchase price.`
    : financesNegativeEquity
      ? `Part of the ${money(
          net
        )} you still owe on your trade-in has been added to your new Carvana loan. The remaining balance will be added to your down payment.`
      : `The ${money(
          net
        )} you still owe on your trade-in will be added to your total purchase price, and Carvana handles your loan payoff.`;

  return {
    isDeclined,
    declinedCopy,
    label: isNegativeEquity ? 'Trade-in balance due' : 'Trade-in value applied',
    amount: isNegativeEquity ? Math.abs(net) : -net,
    isNegativeEquity,
    showPayoff: !isDeclined && lienAmount > 0,
    payoffRows: isNegativeEquity
      ? [balanceRow, offerRow, resultRow]
      : [offerRow, balanceRow, resultRow],
    payoffBody
  };
};

export default deriveTradeIn;
